/**
 * Canonical, single source of truth for every user-selectable model.
 *
 * Each model entry carries BOTH wiring capabilities (label, supportsImages,
 * webAccess, legacy, supportsReasoningEffort) AND display metadata used by the
 * model picker (releasedAt, costTier, description). Do not split these into a
 * parallel map keyed by the same modelId — keep one object per model here.
 *
 * Release dates are sourced from provider announcements (OpenAI, Anthropic,
 * Google). costTier is a relative 1–4 scale ($ to $$$$) for UI comparison, not
 * exact pricing.
 */
export const MODEL_CONFIG = {
  OPENAI: {
    logoPath: '/svgs/openai-logo.svg',
    'gpt-3.5-turbo': {
      label: 'gpt-3.5-turbo',
      supportsImages: true,
      webAccess: false,
      legacy: true,
      releasedAt: '2023-03-01',
      costTier: 1,
      description:
        'Legacy fast chat model. Kept for older agents; newer GPT-5 models are recommended.',
    },
    'gpt-4-turbo': {
      label: 'gpt-4-turbo',
      supportsImages: true,
      webAccess: false,
      legacy: true,
      releasedAt: '2024-04-09',
      costTier: 2,
      description: 'Legacy GPT-4 Turbo variant for text and vision workloads.',
    },
    'gpt-4-turbo-vision': {
      label: 'gpt-4-turbo-vision',
      supportsImages: true,
      webAccess: false,
      legacy: true,
      releasedAt: '2023-11-06',
      costTier: 2,
      description: 'Legacy vision-focused GPT-4 Turbo endpoint.',
    },
    'gpt-4o': {
      label: 'gpt-4o',
      supportsImages: true,
      webAccess: false,
      legacy: true,
      releasedAt: '2024-05-13',
      costTier: 2,
      description:
        'Legacy multimodal flagship. Strong general chat before the GPT-5 generation.',
    },
    'gpt-4o-mini': {
      label: 'gpt-4o-mini',
      isDefault: false,
      supportsImages: true,
      webAccess: false,
      legacy: true,
      releasedAt: '2024-07-18',
      costTier: 1,
      description: 'Legacy smaller GPT-4o variant for lower-cost chat.',
    },
    'gpt-4.1': {
      label: 'gpt-4.1',
      supportsImages: true,
      webAccess: false,
      legacy: true,
      releasedAt: '2025-04-14',
      costTier: 2,
      description: 'Legacy GPT-4.1 family model for coding and instruction following.',
    },
    'gpt-4.1-mini': {
      label: 'gpt-4.1-mini',
      supportsImages: true,
      webAccess: false,
      legacy: true,
      releasedAt: '2025-04-14',
      costTier: 1,
      description: 'Legacy mid-size GPT-4.1 model balancing cost and quality.',
    },
    'gpt-4.1-nano': {
      label: 'gpt-4.1-nano',
      supportsImages: true,
      webAccess: false,
      legacy: true,
      releasedAt: '2025-04-14',
      costTier: 1,
      description: 'Legacy lightweight GPT-4.1 model for simple tasks.',
    },
    'gpt-5': {
      label: 'gpt-5',
      supportsImages: true,
      webAccess: false,
      legacy: false,
      supportsReasoningEffort: true,
      releasedAt: '2025-08-07',
      costTier: 3,
      description:
        'OpenAI reasoning model for coding and agentic tasks. Available in standard, mini, and nano sizes.',
    },
    'gpt-5-mini': {
      label: 'gpt-5-mini',
      supportsImages: true,
      webAccess: false,
      legacy: false,
      supportsReasoningEffort: true,
      releasedAt: '2025-08-07',
      costTier: 2,
      description:
        'Smaller GPT-5 reasoning model with lower cost and latency than full GPT-5.',
    },
    'gpt-5-nano': {
      label: 'gpt-5-nano',
      supportsImages: false,
      webAccess: false,
      legacy: false,
      supportsReasoningEffort: true,
      releasedAt: '2025-08-07',
      costTier: 1,
      description:
        'Most cost-efficient GPT-5 reasoning variant for lightweight agent loops.',
    },
    'gpt-5.2': {
      label: 'gpt-5.2',
      supportsImages: true,
      webAccess: false,
      legacy: false,
      supportsReasoningEffort: true,
      releasedAt: '2025-12-11',
      costTier: 3,
      description:
        'Previous-generation OpenAI thinking model with strong coding and tool use.',
    },
    'gpt-5.4': {
      isDefault: true,
      label: 'gpt-5.4',
      supportsImages: true,
      webAccess: false,
      legacy: false,
      supportsReasoningEffort: true,
      releasedAt: '2026-03-05',
      costTier: 3,
      description:
        'Capable frontier model for professional work, coding, and tool-heavy agent sessions.',
    },
    'gpt-5.5': {
      label: 'gpt-5.5',
      supportsImages: true,
      webAccess: false,
      legacy: false,
      supportsReasoningEffort: true,
      releasedAt: '2026-04-23',
      costTier: 4,
      description:
        "OpenAI's latest frontier model for agentic coding, computer use, and complex knowledge work.",
    },
    o3: {
      label: 'o3',
      supportsImages: true,
      webAccess: false,
      legacy: false,
      supportsReasoningEffort: true,
      releasedAt: '2025-04-16',
      costTier: 4,
      description:
        'Dedicated reasoning model with tool use during its chain of thought. Best for hard math, coding, and science.',
    },
    'o3-mini': {
      label: 'o3-mini',
      supportsImages: false,
      webAccess: false,
      legacy: true,
      releasedAt: '2025-01-31',
      costTier: 2,
      description: 'Legacy smaller o-series reasoning model.',
    },
    'o4-mini': {
      label: 'o4-mini',
      supportsImages: true,
      webAccess: false,
      legacy: false,
      supportsReasoningEffort: true,
      releasedAt: '2025-04-16',
      costTier: 2,
      description:
        'Compact reasoning model with vision and full tool capabilities at lower cost than o3.',
    },
    'o1-preview': {
      label: 'o1-preview',
      supportsImages: false,
      webAccess: false,
      legacy: true,
      releasedAt: '2024-09-12',
      costTier: 3,
      description: 'Legacy first-generation OpenAI reasoning preview model.',
    },
    'gpt-4o-search-preview': {
      label: 'gpt-4o-search-preview',
      supportsImages: false,
      webAccess: true,
      legacy: true,
      releasedAt: '2025-03-11',
      costTier: 2,
      description: 'Legacy GPT-4o variant with built-in web search preview.',
    },
    'gpt-4o-mini-search-preview': {
      label: 'gpt-4o-mini-search-preview',
      supportsImages: false,
      webAccess: true,
      legacy: true,
      releasedAt: '2025-03-11',
      costTier: 1,
      description: 'Legacy smaller GPT-4o model with web search preview.',
    },
  },
  ANTHROPIC: {
    logoPath: '/svgs/anthropic-logo.svg',
    'claude-3-5-haiku-20241022': {
      label: 'Claude 3.5 Haiku',
      supportsImages: true,
      webAccess: false,
      legacy: true,
      releasedAt: '2024-10-22',
      costTier: 1,
      description: 'Legacy fast Claude 3.5 Haiku snapshot.',
    },
    'claude-3-5-sonnet-20241022': {
      label: 'Claude 3.5 Sonnet (2024-10-22)',
      supportsImages: true,
      webAccess: false,
      legacy: true,
      releasedAt: '2024-10-22',
      costTier: 2,
      description: 'Legacy Claude 3.5 Sonnet snapshot for general assistant work.',
    },
    'claude-3-7-sonnet-20250219': {
      label: 'Claude 3.7 Sonnet',
      supportsImages: true,
      webAccess: false,
      legacy: true,
      supportsReasoningEffort: true,
      releasedAt: '2025-02-19',
      costTier: 2,
      description:
        'Legacy Claude 3.7 Sonnet with extended thinking for harder reasoning tasks.',
    },
    'claude-sonnet-4-20250514': {
      label: 'Claude Sonnet 4',
      supportsImages: true,
      webAccess: false,
      legacy: false,
      supportsReasoningEffort: true,
      releasedAt: '2025-05-14',
      costTier: 2,
      description:
        'Claude Sonnet 4 for high-quality coding, analysis, and everyday agent work.',
    },
    'claude-sonnet-4-5-20250929': {
      label: 'Claude Sonnet 4.5',
      supportsImages: true,
      webAccess: false,
      legacy: true,
      supportsReasoningEffort: true,
      releasedAt: '2025-09-29',
      costTier: 2,
      description: 'Legacy Claude Sonnet 4.5 snapshot with improved coding performance.',
    },
    'claude-haiku-4-5-20251001': {
      label: 'Claude Haiku 4.5',
      supportsImages: true,
      webAccess: false,
      legacy: false,
      supportsReasoningEffort: true,
      releasedAt: '2025-10-01',
      costTier: 1,
      description:
        'Fast Claude Haiku 4.5 model for responsive agents and high-volume tasks.',
    },
    'claude-opus-4-5-20251101': {
      label: 'Claude Opus 4.5',
      supportsImages: true,
      webAccess: false,
      legacy: true,
      supportsReasoningEffort: true,
      releasedAt: '2025-11-01',
      costTier: 4,
      description: 'Legacy Claude Opus 4.5 flagship for the hardest problems.',
    },
    'claude-sonnet-4-6': {
      label: 'Claude Sonnet 4.6',
      supportsImages: true,
      webAccess: false,
      legacy: false,
      supportsReasoningEffort: true,
      releasedAt: '2026-02-17',
      costTier: 2,
      description:
        'Balanced Anthropic model with strong coding, computer use, and 1M-token context.',
    },
    'claude-opus-4-6': {
      label: 'Claude Opus 4.6',
      supportsImages: true,
      webAccess: false,
      legacy: false,
      supportsReasoningEffort: true,
      releasedAt: '2026-02-05',
      costTier: 4,
      description:
        'High-capability Opus model for complex agents, long outputs, and precision work.',
    },
    'claude-opus-4-7': {
      label: 'Claude Opus 4.7',
      supportsImages: true,
      webAccess: false,
      legacy: false,
      supportsReasoningEffort: true,
      releasedAt: '2026-04-16',
      costTier: 4,
      description:
        "Anthropic's frontier Opus model with top-tier coding, vision, and multi-step reasoning.",
    },
    'claude-opus-4-8': {
      label: 'Claude Opus 4.8',
      supportsImages: true,
      webAccess: false,
      legacy: false,
      supportsReasoningEffort: true,
      releasedAt: '2026-05-28',
      costTier: 4,
      description:
        "Anthropic's latest Opus flagship for agentic coding, long-running workflows, and complex knowledge work.",
    },
  },
  GOOGLE: {
    logoPath: '/svgs/google-ai-studio-logo.svg',
    'gemini-2.0-flash': {
      label: 'gemini-2.0-flash',
      supportsImages: false,
      webAccess: false,
      legacy: false,
      releasedAt: '2024-12-11',
      costTier: 1,
      description:
        'Fast Gemini 2.0 model for low-latency chat and lightweight workflows.',
    },
    'gemini-2.5-flash': {
      label: 'gemini-2.5-flash',
      supportsImages: true,
      webAccess: false,
      legacy: false,
      supportsReasoningEffort: true,
      releasedAt: '2025-06-17',
      costTier: 1,
      description:
        "Google's fast thinking model with strong speed-to-quality balance for agents.",
    },
    'gemini-2.5-pro': {
      label: 'gemini-2.5-pro',
      supportsImages: true,
      webAccess: false,
      legacy: false,
      supportsReasoningEffort: true,
      releasedAt: '2025-06-17',
      costTier: 2,
      description:
        "Google's advanced reasoning model for coding, analysis, and complex tasks.",
    },
    'gemini-3.1-pro-preview': {
      label: 'gemini-3.1-pro-preview',
      supportsImages: true,
      webAccess: false,
      legacy: false,
      supportsReasoningEffort: true,
      releasedAt: '2026-02-19',
      costTier: 3,
      description:
        "Preview of Google's Gemini 3.1 Pro for frontier reasoning and multimodal work.",
    },
    'gemini-3.5-flash': {
      label: 'gemini-3.5-flash',
      supportsImages: true,
      webAccess: false,
      legacy: false,
      supportsReasoningEffort: true,
      releasedAt: '2026-05-19',
      costTier: 2,
      description:
        'Latest Gemini Flash model built for agentic coding and long-horizon tasks.',
    },
  },
};

export const AVAILABLE_MODELS = Object.values(MODEL_CONFIG).flatMap(models => Object.keys(models));

export const DEFAULT_MODEL =
  Object.entries(MODEL_CONFIG)
    .flatMap(([_, models]) => Object.entries(models))
    .find(([_, config]) => config.isDefault)?.[0] || 'gpt-5.4';

export const REASONING_EFFORT_DEFAULT = 'MEDIUM';
export const REASONING_EFFORT_OPTIONS = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

export const getModelConfig = modelId => {
  for (const [, models] of Object.entries(MODEL_CONFIG)) {
    const config = models[modelId];
    if (config && typeof config === 'object') return config;
  }
  return null;
};

/** Model id used at runtime when an agent record has no explicit model. */
export function resolveAgentModelId(agent) {
  const model = String(agent?.model || '').trim();
  return model || DEFAULT_MODEL;
}

/** Human-readable model label for agent cards and team node summaries. */
export function resolveAgentModelLabel(agent) {
  const modelId = resolveAgentModelId(agent);
  return getModelConfig(modelId)?.label || modelId;
}

export const supportsReasoningEffort = modelId =>
  getModelConfig(modelId)?.supportsReasoningEffort === true;
